/**
 * 认证相关的存储工具
 * 管理 token 和用户信息的存储
 */

import type { UserInfo } from "../api/auth";
import { STORAGE_KEYS } from "../constants";
import { getCookie, removeCookie, setCookie } from "./cookies";
import { storage } from "./storage";

export const AUTH_STATE_CHANGE_EVENT = "auth-state-change";

function notifyAuthStateChange(): void {
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
    } catch {
      // ignore in SSR / non-browser environments
    }
  }
}

/**
 * 保存认证 token（仅使用 cookies，支持同域名和子域名）
 */
export function saveToken(token: string): boolean {
  try {
    setCookie(STORAGE_KEYS.AUTH_TOKEN, token, 30); // 30 天过期

    // 同时保存到 localStorage 作为备用（向后兼容）
    storage.set(STORAGE_KEYS.AUTH_TOKEN, token);
    notifyAuthStateChange();
    // 暂停 localStorage 写入，避免与共享 Cookie 形成两套登录状态。
    // storage.set(STORAGE_KEYS.AUTH_TOKEN, token);
    return true;
  } catch (error) {
    console.error("Failed to save token to cookie:", error);
    return false;
  }
}

/**
 * 获取认证 token（仅从 cookies 读取，localStorage 回退暂时停用）
 */
export function getToken(): string | null {
  // 优先从 cookies 读取
  const cookieToken = getCookie(STORAGE_KEYS.AUTH_TOKEN);
  if (cookieToken) {
    return cookieToken;
  }
  // 暂停 localStorage 回退，避免官网和子应用对登录状态判断不一致。
  // return storage.get<string>(STORAGE_KEYS.AUTH_TOKEN);
  return null;
}

/**
 * 删除认证 token（仅删除 cookie，localStorage 清理暂时停用）
 */
export function removeToken(): boolean {
  try {
    removeCookie(STORAGE_KEYS.AUTH_TOKEN);

    storage.remove(STORAGE_KEYS.AUTH_TOKEN);
    notifyAuthStateChange();
    // storage.remove(STORAGE_KEYS.AUTH_TOKEN);
    return true;
  } catch (error) {
    console.error("Failed to remove token from cookie:", error);
    return false;
  }
}

/**
 * 保存用户信息
 */
export function saveUserInfo(userInfo: UserInfo): boolean {
  return storage.set(STORAGE_KEYS.USER_INFO, userInfo);
}

/**
 * 获取用户信息
 */
export function getUserInfo(): UserInfo | null {
  return storage.get<UserInfo>(STORAGE_KEYS.USER_INFO);
}

/**
 * 删除用户信息
 */
export function removeUserInfo(): boolean {
  return storage.remove(STORAGE_KEYS.USER_INFO);
}

/**
 * 保存 OAuth code
 */
export function saveOAuthCode(code: string): boolean {
  return storage.set(STORAGE_KEYS.OAUTH_CODE, code);
}

/**
 * 获取 OAuth code
 */
export function getOAuthCode(): string | null {
  return storage.get<string>(STORAGE_KEYS.OAUTH_CODE);
}

/**
 * 删除 OAuth code
 */
export function removeOAuthCode(): boolean {
  return storage.remove(STORAGE_KEYS.OAUTH_CODE);
}

/**
 * 清除所有认证相关数据
 */
export function clearAuth(): void {
  removeToken();
  removeUserInfo();
  removeOAuthCode();
}

/**
 * 检查是否已登录
 */
export function isAuthenticated(): boolean {
  return getToken() !== null;
}
